import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useClientes } from '@/components/hooks/useClientes';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}

const AddClientDialog: React.FC<Props> = ({ open, onOpenChange, onAdded }) => {
  const { t } = useTranslation();
  const empresaId = localStorage.getItem('id_empresa') || '';
  const { buscarClienteVista, agregarCliente } = useClientes();

  const [query, setQuery] = useState('');
  const [cliente, setCliente] = useState<any | null>(null);
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setCliente(null);
      setQuery('');
      setChecking(false);
      setAdding(false);
    }
    onOpenChange(open);
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error(t('addClientDialog.errorEmpty'));
      return;
    }
    setChecking(true);
    try {
      const found = await buscarClienteVista(query.trim());
      if (!found) {
        setCliente(null);
        toast.error(t('addClientDialog.errorNotFound'));
        return;
      }
      setCliente(found);
      toast.success(t('addClientDialog.successFound'));
    } finally {
      setChecking(false);
    }
  };

  const handleAdd = async () => {
    if (!cliente) return;
    setAdding(true);
    try {
      await agregarCliente(empresaId, cliente.email, undefined, () => {
        onAdded();
      });
      handleOpenChange(false);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {t('addClientDialog.title')}
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={() => handleOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4 text-sm">
            <Label className="text-gray-700 dark:text-gray-300">
              {t('addClientDialog.searchLabel')}
            </Label>

            <div className="flex gap-2 items-center">
              <Input
                type="text"
                placeholder={t('addClientDialog.searchPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
              <Button
                type="button"
                onClick={handleSearch}
                disabled={checking}
                variant="outline"
                className="h-10 w-10 p-0 flex items-center justify-center dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>

            {cliente && (
              <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm border border-green-200 dark:border-green-800 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p>
                      {t('addClientDialog.clientLabel')}
                      <strong className="ml-1">{cliente.nombre}</strong>
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      {cliente.email || '-'} / {cliente.telefono || '-'}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCliente(null)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    {t('addClientDialog.remove')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('addClientDialog.cancel')}
          </Button>

          <Button
            onClick={handleAdd}
            disabled={adding || !cliente}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {adding ? t('addClientDialog.adding') : t('addClientDialog.addBtn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientDialog;